/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * reCAPTCHA token generation: cache-aware public API plus the core flow with
 * stale-session reload, loader readiness, and bounded retries.
 */

import { awaitRecaptchaReady, AntiBotUnavailableError, reloadRecaptchaSDK } from './recaptcha-loader';
import {
  getSDKSessionAgeMs,
  resetSDKSessionTimestamp,
  SDK_MAX_SESSION_AGE_MS,
} from './recaptcha-session-tracker';
import {
  isRecaptchaDisabled,
  getRecaptchaSiteKey,
  getGrecaptchaEnterprise,
  resolveAntiBotBypassToken,
} from './recaptcha-config';
import { reportRecaptchaToInspector, checkRecaptchaSimulation } from './recaptcha-devcockpit';
import {
  ANTIBOT_LOG,
  invalidateAntiBotTokenCache,
} from './recaptcha-execute';
import { executeWithRetries } from './recaptcha-retry';

// Must comfortably exceed executeWithRetries' own worst case
// (MAX_ATTEMPTS * EXECUTE_TIMEOUT_MS + retry delays, ~16.5s), or this outer
// race cuts the retry loop off before it gets its second attempt -- observed
// on testing, where a single execute() round-trip can itself take several
// seconds.
const TOTAL_TIMEOUT_MS = 20_000;

/**
 * Generate a reCAPTCHA Enterprise token for an API request.
 *
 * Always mints a fresh token via grecaptcha.enterprise.execute(). Tokens are
 * single-use on the server; reusing a cached value across requests causes
 * TOKEN_PROPERTIES_INVALID → HTTP 403 AntiBotInvalidTokenError in production.
 *
 * @throws {AntiBotUnavailableError} when the script cannot load or token
 *         generation fails after retries.
 * Returns an empty string only when reCAPTCHA is explicitly disabled.
 */
export async function generateAntiBotToken(action: string = 'api_request'): Promise<string> {
  if (isRecaptchaDisabled()) return resolveAntiBotBypassToken() ?? '';

  invalidateAntiBotTokenCache();
  return _generateAntiBotTokenCore(action);
}

/**
 * Runs the full token flow with a hard total-timeout guard, rejecting with
 * {@link AntiBotUnavailableError} if it exceeds `TOTAL_TIMEOUT_MS`.
 *
 * @param action Action the token is generated for.
 * @returns A promise resolving to the generated token.
 */
async function _generateAntiBotTokenCore(action: string): Promise<string> {
  const tokenPromise = _generateAntiBotTokenCoreInner(action);
  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new AntiBotUnavailableError(
      `Total anti-bot flow timed out after ${TOTAL_TIMEOUT_MS}ms`,
    )), TOTAL_TIMEOUT_MS),
  );

  return Promise.race([tokenPromise, timeoutPromise]);
}

/**
 * Proactively reloads the SDK when its session exceeds the max age, to avoid
 * Google-side session expiry producing invalid tokens. Failures are swallowed
 * so the caller can still attempt token generation.
 *
 * @param siteKey Site key used to reload the SDK.
 */
async function _maybeReloadStaleSession(siteKey: string): Promise<void> {
  if (getSDKSessionAgeMs() <= SDK_MAX_SESSION_AGE_MS) return;

  console.warn(ANTIBOT_LOG, 'SDK session stale — proactive reload', {
    session_age_ms: getSDKSessionAgeMs(),
    threshold_ms: SDK_MAX_SESSION_AGE_MS,
  });
  invalidateAntiBotTokenCache();
  try {
    await reloadRecaptchaSDK(siteKey);
    resetSDKSessionTimestamp();
  } catch (err) {
    console.warn(ANTIBOT_LOG, 'Proactive SDK reload failed, continuing with existing session', {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/**
 * Core token flow (without the outer timeout race): resolves the site key,
 * short-circuits on simulation, refreshes a stale session, awaits SDK readiness,
 * then runs the bounded execute-with-retries loop.
 *
 * @param action Action the token is generated for.
 * @returns A promise resolving to the generated token ('' when simulated).
 * @throws {AntiBotUnavailableError} When the site key is missing or the
 *   Enterprise handle is unavailable after the loader resolves.
 */
async function _generateAntiBotTokenCoreInner(action: string): Promise<string> {
  const siteKey = getRecaptchaSiteKey();
  if (checkRecaptchaSimulation(siteKey ?? '')) return '';
  if (!siteKey) {
    const err = new AntiBotUnavailableError('ANTI_BOT_PROVIDER_ID is not configured');
    console.error(ANTIBOT_LOG, 'Site key not configured', {
      window_env_keys: Object.keys((window as any).__ENV__ || {}),
      import_meta_env: typeof import.meta?.env?.ANTI_BOT_PROVIDER_ID,
    });
    throw err;
  }

  const tokenStartTime = Date.now();

  await _maybeReloadStaleSession(siteKey);
  await awaitRecaptchaReady();

  const enterprise = getGrecaptchaEnterprise();
  if (!enterprise) {
    const diag = {
      grecaptcha_exists: typeof (window as any).grecaptcha !== 'undefined',
      enterprise_exists: typeof (window as any).grecaptcha?.enterprise !== 'undefined',
      elapsed_ms: Date.now() - tokenStartTime,
      action,
      site_key_prefix: siteKey.slice(0, 10),
    };
    console.error(ANTIBOT_LOG, 'Enterprise not available after loader resolved', diag);
    const err = new AntiBotUnavailableError('reCAPTCHA Enterprise not available after loader resolved');
    reportRecaptchaToInspector(siteKey, 0, err.message);
    throw err;
  }

  return executeWithRetries(enterprise, siteKey, action, tokenStartTime);
}

/**
 * Reload the reCAPTCHA SDK and generate a completely fresh token.
 *
 * Used when a normal retry with `generateAntiBotToken` still produces invalid tokens
 * because the SDK's internal session with Google servers has expired.
 * This forces a full script reload (equivalent to a hard page refresh).
 */
export async function generateAntiBotTokenWithReload(action: string = 'api_request'): Promise<string> {
  if (isRecaptchaDisabled()) return resolveAntiBotBypassToken() ?? '';

  invalidateAntiBotTokenCache();

  const siteKey = getRecaptchaSiteKey();
  if (!siteKey) {
    console.warn(ANTIBOT_LOG, 'Cannot reload SDK — site key not configured');
    return '';
  }

  console.warn(ANTIBOT_LOG, 'Generating token after full SDK reload', {
    action,
    site_key_prefix: siteKey.slice(0, 10),
  });

  try {
    await reloadRecaptchaSDK(siteKey);
    resetSDKSessionTimestamp();
  } catch (err) {
    console.error(ANTIBOT_LOG, 'SDK reload failed, attempting token generation without reload', {
      error: err instanceof Error ? err.message : String(err),
    });
  }

  return generateAntiBotToken(action);
}
